import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { materialsApi, type Material } from '@/api/materials';
import { humanizeError } from '@/lib/errors';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { MaterialCard, MaterialModal, MaterialRows } from '@/pages/admin/AdminMaterialsPage';
import { useConfirm } from '@/components/ui/ConfirmProvider';
import { useIsMobile } from '@/hooks/useIsMobile';
import { MAdd, MEmpty, MTitle } from '@/components/mobile/MobileKit';

/**
 * Curator materials — same editor as the admin page but scope is forced to
 * `curator` (the server binds curator_id to the caller and ownership-checks
 * mutations). Lists only the curator's own materials via /curator/materials.
 */
export function CuratorMaterialsPage() {
  const ask = useConfirm();
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [creatingOpen, setCreatingOpen] = useState(false);
  const [editing, setEditing] = useState<Material | null>(null);
  const isMobile = useIsMobile();

  const list = useQuery({ queryKey: ['curator', 'materials'], queryFn: () => materialsApi.listCurator() });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['curator', 'materials'] });
  const remove = useMutation({
    mutationFn: (id: string) => materialsApi.removeCurator(id),
    onSuccess: invalidate,
  });

  const sorted = [...(list.data ?? [])].sort(
    (a, b) => (a.display_order ?? 0) - (b.display_order ?? 0),
  );
  const confirmDelete = async (m: Material) => {
    if (await ask({ message: t('materialsAdmin.deleteConfirm', { title: m.title }), danger: true })) {
      remove.mutate(m.id);
    }
  };
  const modals = (
    <>
      {creatingOpen && (
        <MaterialModal forceCuratorScope onClose={() => setCreatingOpen(false)} onSaved={invalidate} />
      )}
      {editing && (
        <MaterialModal
          material={editing}
          forceCuratorScope
          onClose={() => setEditing(null)}
          onSaved={invalidate}
        />
      )}
    </>
  );

  // ── Телефон (docs/CURATOR-MOBILE-DESIGN.md §4.17) ─────────────────────────
  if (isMobile) {
    return (
      <div className="space-y-3">
        <MTitle
          title={t('materialsAdmin.curatorTitle')}
          subtitle={t('materialsAdmin.curatorSubtitle')}
          right={<MAdd label={t('materialsAdmin.createBtn')} onClick={() => setCreatingOpen(true)} />}
        />
        {remove.error && <p className="px-0.5 text-[13px] text-error">{humanizeError(remove.error)}</p>}
        {list.isLoading ? (
          <p className="px-0.5 text-[13px] text-on-surface-variant">{t('common.loading')}</p>
        ) : list.error ? (
          <p className="px-0.5 text-[13px] text-error">{humanizeError(list.error)}</p>
        ) : !sorted.length ? (
          <MEmpty icon="video_library" title={t('materialsAdmin.empty')} />
        ) : (
          <MaterialRows
            items={sorted}
            scopeLabelOf={() => t('materialsAdmin.scopeCurator')}
            onEdit={setEditing}
            onDelete={confirmDelete}
          />
        )}
        {modals}
      </div>
    );
  }

  return (
    <>
      <PageHeader
        title={t('materialsAdmin.curatorTitle')}
        subtitle={t('materialsAdmin.curatorSubtitle')}
        actions={
          <Button onClick={() => setCreatingOpen(true)}>
            <span className="material-symbols-outlined">add</span>
            {t('materialsAdmin.createBtn')}
          </Button>
        }
      />

      {remove.error && (
        <Card variant="subtle" className="mb-4 text-sm text-error">{humanizeError(remove.error)}</Card>
      )}

      {list.isLoading ? (
        <Card variant="subtle">{t('common.loading')}</Card>
      ) : list.error ? (
        <Card variant="subtle" className="text-error">{humanizeError(list.error)}</Card>
      ) : !sorted.length ? (
        <Card variant="subtle" className="text-center text-on-surface-variant">
          {t('materialsAdmin.empty')}{' '}
          <button type="button" onClick={() => setCreatingOpen(true)} className="font-semibold text-primary">
            {t('materialsAdmin.createFirst')}
          </button>
        </Card>
      ) : (
        <ul className="grid gap-3 md:grid-cols-3 xl:grid-cols-4">
          {sorted.map((m) => (
            <li key={m.id}>
              <MaterialCard
                m={m}
                scopeLabelOverride={t('materialsAdmin.scopeCurator')}
                onEdit={() => setEditing(m)}
                onDelete={() => confirmDelete(m)}
              />
            </li>
          ))}
        </ul>
      )}

      {modals}
    </>
  );
}
